import { useState, useCallback, useEffect, useMemo } from "react";
import { TextField, Button } from "@mui/material";
import { BrandLoader } from "../../components/Brand";
import { getBrokerRequirements } from "../../api/users";
import styles from "./AddFamilyMemberModal.module.css";

const initialForm = {
  name: "",
  broker: "",
  clientId: "",
  emailId: "",
  panNumber: "",
  dob: "",
  familyTag: "",
  brokerExtraFields: {},
};

function extraToFieldMap(extra) {
  const out = {};
  if (!extra || typeof extra !== "object") return out;
  for (const [k, v] of Object.entries(extra)) {
    out[k] = v == null ? "" : String(v);
  }
  return out;
}

function clientToForm(client) {
  const extra = client.broker_details_extra_fields || {};
  return {
    name: client.full_name || "",
    broker: client.broker_name || "",
    clientId: client.broker_client_code || client.username || "",
    emailId: client.email || "",
    panNumber: client.pan_number || "",
    dob: client.dob ? client.dob.split("T")[0] : "",
    familyTag: (client.family_tag || "").toUpperCase(),
    brokerExtraFields: extraToFieldMap(extra),
  };
}

/** When API has no definition for this broker, still allow editing stored keys (legacy). */
function fallbackFieldsFromExtra(extra) {
  if (!extra || typeof extra !== "object") return [];
  return Object.keys(extra).map((input_name) => ({
    input_name,
    type: "text",
    label: input_name.replace(/_/g, " "),
    placeholder: "",
  }));
}

function AddFamilyMemberModal({ isOpen, onClose, onAdd, editClient, onEdit }) {
  const isEdit = Boolean(editClient);
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [schemaData, setSchemaData] = useState(null);
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [requirementsError, setRequirementsError] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(editClient ? clientToForm(editClient) : initialForm);
    setError("");
    setSubmitting(false);
  }, [isOpen, editClient]);

  useEffect(() => {
    if (!isOpen) return;
    const ac = new AbortController();
    setRequirementsLoading(true);
    setRequirementsError(false);
    getBrokerRequirements({ signal: ac.signal })
      .then((data) => {
        setSchemaData(data && typeof data.schema === "object" ? data : { schema: {} });
      })
      .catch(() => {
        if (!ac.signal.aborted) {
          setRequirementsError(true);
          setSchemaData({ schema: {} });
        }
      })
      .finally(() => {
        if (!ac.signal.aborted) setRequirementsLoading(false);
      });
    return () => ac.abort();
  }, [isOpen]);

  const brokerCodes = useMemo(() => {
    const s = schemaData?.schema;
    if (!s || typeof s !== "object") return [];
    return Object.keys(s).sort();
  }, [schemaData]);

  useEffect(() => {
    if (!isOpen || isEdit || requirementsLoading || brokerCodes.length === 0) return;
    setForm((prev) => {
      if (prev.broker) return prev;
      return { ...prev, broker: brokerCodes[0], brokerExtraFields: {} };
    });
  }, [isOpen, isEdit, requirementsLoading, brokerCodes]);

  const brokerFieldDefs = useMemo(() => {
    const broker = form.broker;
    if (!broker) return [];
    const fromApi = schemaData?.schema?.[broker];
    if (Array.isArray(fromApi) && fromApi.length > 0) return fromApi;
    if (isEdit && editClient?.broker_name === broker) {
      return fallbackFieldsFromExtra(editClient.broker_details_extra_fields);
    }
    return [];
  }, [schemaData, form.broker, isEdit, editClient]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    const nextValue = name === "familyTag" ? value.toUpperCase() : value;
    setForm((prev) => ({ ...prev, [name]: nextValue }));
    setError("");
  }, []);

  const handleBrokerChange = useCallback((e) => {
    const { value } = e.target;
    setForm((prev) => ({
      ...prev,
      broker: value,
      brokerExtraFields: {},
    }));
    setError("");
  }, []);

  const handleBrokerExtraChange = useCallback((inputName, value) => {
    setForm((prev) => ({
      ...prev,
      brokerExtraFields: { ...prev.brokerExtraFields, [inputName]: value },
    }));
    setError("");
  }, []);

  const buildExtraObject = useCallback(() => {
    const extra = {};
    for (const f of brokerFieldDefs) {
      const key = f.input_name;
      const raw = form.brokerExtraFields[key];
      extra[key] = raw == null ? "" : String(raw).trim();
    }
    return extra;
  }, [brokerFieldDefs, form.brokerExtraFields]);

  const validate = useCallback(() => {
    if (!form.name?.trim()) return "Name is required.";
    if (!form.broker) return "Please select a Broker.";
    if (!form.clientId?.trim()) return "Client Id is required.";
    if (!form.emailId?.trim()) return "Email Id is required.";
    if (!form.panNumber?.trim()) return "Pan Number is required.";
    if (!form.dob?.trim()) return "DOB is required.";

    if (requirementsError && !isEdit && brokerCodes.length === 0) {
      return "Broker list could not be loaded. Close and try again.";
    }

    for (const f of brokerFieldDefs) {
      const v = form.brokerExtraFields[f.input_name];
      if (!String(v ?? "").trim()) {
        return `${f.label || f.input_name} is required.`;
      }
    }

    return null;
  }, [form, brokerFieldDefs, requirementsError, isEdit, brokerCodes.length]);

  const buildEditPayload = useCallback(() => {
    const extra = { ...(editClient.broker_details_extra_fields || {}) };
    const built = buildExtraObject();
    for (const key of Object.keys(built)) {
      extra[key] = built[key];
    }
    return {
      full_name: form.name.trim(),
      email: form.emailId.trim(),
      broker_name: form.broker,
      is_active: editClient.is_active === true,
      pan_number: form.panNumber.trim(),
      dob: form.dob,
      family_tag: form.familyTag.trim(),
      password: "0",
      password2: "0",
      broker_details_extra_fields: extra,
    };
  }, [editClient, form, buildExtraObject]);

  const buildCreatePayload = useCallback(() => {
    return {
      full_name: form.name.trim(),
      email: form.emailId.trim(),
      password: "0",
      password2: "0",
      broker_client_code: form.clientId.trim(),
      pan_number: form.panNumber.trim(),
      dob: form.dob,
      broker_name: form.broker,
      family_tag: form.familyTag.trim(),
      broker_details_extra_fields: buildExtraObject(),
    };
  }, [form, buildExtraObject]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const validationError = validate();
      if (validationError) {
        setError(validationError);
        return;
      }

      setSubmitting(true);
      try {
        if (isEdit && onEdit) {
          await onEdit(editClient.id, buildEditPayload());
          setForm(initialForm);
          setError("");
          onClose();
          return;
        }

        await onAdd(buildCreatePayload());
        setForm(initialForm);
        setError("");
        onClose();
      } catch (err) {
        setError(err?.response?.data?.detail || "Failed to save client");
      } finally {
        setSubmitting(false);
      }
    },
    [validate, isEdit, editClient, onEdit, buildEditPayload, buildCreatePayload, onAdd, onClose]
  );

  const handleClose = useCallback(() => {
    if (submitting) return;
    setForm(initialForm);
    setError("");
    onClose();
  }, [onClose, submitting]);

  const showBrokerDetails = form.broker && brokerFieldDefs.length > 0;

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={submitting ? undefined : handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{isEdit ? "Edit Client" : "Add Client"}</h2>
          <button type="button" className={styles.closeBtn} onClick={handleClose} disabled={submitting} aria-label="Close">
            <svg className={styles.closeIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <p className={styles.error}>{error}</p>}
          {requirementsError && (
            <p className={styles.error} role="status">
              Could not load broker list from the server. You can still try again after closing and reopening.
            </p>
          )}

          <fieldset disabled={submitting} className={styles.formFieldset}>
          <div className={styles.sectionLabel}>Client details</div>
          <div className={styles.sectionBlock}>
          <div className={styles.grid}>
            <div className={styles.field}>
              <label className={styles.label}>Name</label>
              <TextField
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. abcd"
                fullWidth
                size="small"
                className={styles.input}
                variant="outlined"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Broker</label>
              <div className={styles.brokerSelectRow}>
                {requirementsLoading ? (
                  <BrandLoader variant="icon" size={20} tone="surface" className={styles.brokerSelectSpinner} />
                ) : null}
                <select
                  name="broker"
                  value={form.broker}
                  onChange={handleBrokerChange}
                  className={styles.select}
                  disabled={isEdit || requirementsLoading}
                >
                  <option value="">Select broker</option>
                  {form.broker && !brokerCodes.includes(form.broker) ? (
                    <option value={form.broker}>{form.broker}</option>
                  ) : null}
                  {brokerCodes.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Client Id</label>
              <TextField
                name="clientId"
                value={form.clientId}
                onChange={handleChange}
                placeholder="e.g. 1234"
                fullWidth
                size="small"
                className={styles.input}
                variant="outlined"
                disabled={isEdit}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Email Id</label>
              <TextField
                name="emailId"
                type="email"
                value={form.emailId}
                onChange={handleChange}
                placeholder="e.g. abcd@gmail.com"
                fullWidth
                size="small"
                className={styles.input}
                variant="outlined"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Pan Number</label>
              <TextField
                name="panNumber"
                value={form.panNumber}
                onChange={handleChange}
                placeholder="e.g. abcde1234f"
                fullWidth
                size="small"
                className={styles.input}
                variant="outlined"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>DOB</label>
              <TextField
                name="dob"
                type="date"
                value={form.dob}
                onChange={handleChange}
                fullWidth
                size="small"
                className={styles.input}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Family tag</label>
              <TextField
                name="familyTag"
                value={form.familyTag}
                onChange={handleChange}
                placeholder="e.g. FAMILY_TAG"
                inputProps={{ style: { textTransform: "uppercase" } }}
                fullWidth
                size="small"
                className={styles.input}
                variant="outlined"
              />
            </div>
          </div>
          </div>

          {showBrokerDetails && (
            <div className={styles.brokerSection}>
              <div className={styles.sectionLabel}>Broker details (required for this broker)</div>
              <div className={styles.sectionBlock}>
              <div className={styles.grid}>
                {brokerFieldDefs.map((f) => {
                  const inputType = f.type === "password" ? "password" : "text";
                  return (
                    <div className={styles.field} key={f.input_name}>
                      <label className={styles.label}>{f.label || f.input_name}</label>
                      <TextField
                        name={`brokerExtra_${f.input_name}`}
                        type={inputType}
                        value={form.brokerExtraFields[f.input_name] ?? ""}
                        onChange={(e) => handleBrokerExtraChange(f.input_name, e.target.value)}
                        placeholder={f.placeholder || ""}
                        fullWidth
                        size="small"
                        className={styles.input}
                        variant="outlined"
                        autoComplete="off"
                      />
                    </div>
                  );
                })}
              </div>
              </div>
            </div>
          )}

          </fieldset>

          <div className={styles.actions}>
            <Button type="button" variant="outlined" onClick={handleClose} className={styles.cancelBtn} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              className={styles.submitBtn}
              disabled={submitting || requirementsLoading}
              startIcon={submitting ? <BrandLoader variant="icon" size={16} /> : null}
            >
              {submitting ? (isEdit ? "Saving…" : "Adding…") : isEdit ? "Update" : "Add Client"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddFamilyMemberModal;
